<template>
  <v-container class="grey-lighten-3 full-height">
    <v-row justify="center">
      <v-col cols="12" xl="10">
        <v-sheet class="order-paper pa-6" elevation="3" id="printable-area">

          <!-- Header -->
          <div class="header-section d-flex justify-space-between align-start lines">
            <div class="company-info">
              <h4 class="company-name">{{ order?.branch?.company?.name || '-' }}</h4>
              <p class="company-text">{{ order?.branch?.company?.address || '-' }}</p>
              <p class="company-text">
                <v-icon size="small" class="mr-1">mdi-phone-outline</v-icon>
                {{ order?.branch?.company?.mobile || '-' }}
              </p>
              <p class="company-text">
                <v-icon size="small" class="mr-1">mdi-email-outline</v-icon>
                {{ order?.branch?.company?.email || '-' }}
              </p>
              <p class="company-text" v-if="order?.branch?.company?.website">
                <v-icon size="small" class="mr-1">mdi-web</v-icon>
                {{ order.branch.company.website }}
              </p>
            </div>

            <div class="logo-container">
              <v-img
                :src="`${getFileServerUrl.replace(/\/$/, '')}/${order?.branch?.company?.logo || 'uploads/demo_images/user.png'}`"
                max-width="150"
                max-height="70"
                contain
              />
            </div>
          </div>

          <!-- Report Title -->
          <div class="text-center my-6">
            <h2 class="report-title">Buyer Order</h2>
          </div>

          <!-- Buyer & Order Info -->
          <div class="meta-box mb-4">
            <div><strong>Order No:</strong> {{ order.code || '-' }}</div>
            <div><strong>Buyer:</strong> {{ order.buyer?.company_name || '-' }}</div>
            <div><strong>Address:</strong> {{ order.buyer?.company_address || '-' }}</div>
            <div><strong>Mobile:</strong> {{ order.buyer?.company_mobile || '-' }}</div>
            <div><strong>Status:</strong> {{ order.order_status || '-' }}</div>
          </div>

          <div class="meta-box mb-4 d-flex">
            <div class="flex-1"><strong>Season:</strong> {{ order.season || '-' }}</div>
            <div class="flex-1"><strong>Shipment Date:</strong> {{ formatDate(order.shipment_date) }}</div>
            <div class="flex-1"><strong>Shipment Mode:</strong> {{ order.shipment_mode || '-' }}</div>
          </div>

          <!-- Items & Breakdown Table -->
          <h3 class="text-subtitle-1 font-weight-bold mb-2">Items & Breakdown</h3>
          <table class="order-table mb-6">
            <thead>
              <tr>
                <th>SL</th>
                <th>Style</th>
                <th>Order Qty</th>
                <!-- <th>Unit Price</th> -->
                <th>Total Value</th>
                <th>Color</th>
                <th>Size</th>
                <th>Qty</th>
                <th>Allocated</th>
                <th>Remaining</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(item, i) in order.items" :key="item.id">
                <tr v-for="(bd, index) in item.breakdowns" :key="bd.id">
                  <!-- Item Columns -->
                  <td v-if="index === 0" :rowspan="item.breakdowns.length" class="text-center">{{ i + 1 }}</td>
                  <td v-if="index === 0" :rowspan="item.breakdowns.length">
                    {{ item.style?.name || '-' }} <br><small>({{ item.style?.code || '-' }})</small>
                  </td>
                  <td v-if="index === 0" class="text-right" :rowspan="item.breakdowns.length">{{ item.order_quantity || '-' }}</td>
                  <!-- <td v-if="index === 0" class="text-right" :rowspan="item.breakdowns.length">{{ item.unit_price || '-' }}</td> -->
                  <td v-if="index === 0" class="text-right" :rowspan="item.breakdowns.length">{{ item.total_value || '-' }}</td>

                  <!-- Breakdown Columns -->
                  <td>{{ bd.color?.name || '-' }}</td>
                  <td>{{ bd.size?.name || '-' }}</td>
                  <td class="text-right">{{ bd.quantity || '-' }}</td>
                  <td class="text-right">{{ bd.allocated_qty || '-' }}</td>
                  <td class="text-right">{{ bd.remaining_qty || '-' }}</td>
                </tr>
              </template>
            </tbody>
          </table>

        </v-sheet>
      </v-col>
    </v-row>

    <!-- Print Button -->
    <v-row justify="center" class="d-print-none mt-4">
      <v-btn color="primary" prepend-icon="mdi-printer" @click="print">Print</v-btn>
    </v-row>
  </v-container>
</template>

<script>
export default {
  name: "BuyerOrderPrint",
  data() {
    return {
      order: {
        buyer: {},
        items: [],
      },
      loading: false,
    };
  },
  computed: {
    getFileServerUrl() {
      return this.$store.getters.getFileServerUrl;
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return "-";
      const d = new Date(date);
      return `${d.getDate().toString().padStart(2, "0")}/${
        (d.getMonth() + 1).toString().padStart(2, "0")
      }/${d.getFullYear()}`;
    },

    async fetchBuyerOrder(id) {
      this.loading = true;
      try {
        const response = await this.$store.dispatch("getBuyerOrderById", { id });
        this.order = response.data.data;
      } catch (error) {
        console.error("Failed to load buyer order", error);
      } finally {
        this.loading = false;
      }
    },

    print() {
      const printContent = document.getElementById("printable-area").innerHTML;
      const printWindow = window.open("", "_blank", `width=${screen.width},height=${screen.height}`);

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Buyer Order</title>
          <link href="https://cdn.jsdelivr.net/npm/@mdi/font@7.4.47/css/materialdesignicons.min.css" rel="stylesheet">
          <style>
            body { font-family:'Times New Roman', serif; margin:0; padding:15px; background:white; color:#000; }
            .order-paper { width:100%; max-width:100%; margin:auto; padding:15px; }
            .header-section { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:2px solid #eee; padding-bottom:10px; margin-bottom:16px; }
            .company-name { font-size:20px; font-weight:bold; text-transform:uppercase; margin:0 0 4px 0; }
            .company-text { font-size:14px; margin:2px 0; display:flex; align-items:center; }
            .logo-container img { max-height:70px; max-width:150px; }
            .report-title { font-size:20px; font-weight:bold; text-align:center; margin:16px 0; }
            .meta-box { border:1px solid #000; padding:8px; font-size:13px; margin-bottom:16px; }
            .d-flex { display:flex; gap:10px; }
            .flex-1 { flex:1; }
            .order-table { width:100%; border-collapse:collapse; font-size:13px; margin-bottom:24px; }
            .order-table th, .order-table td { border:1px solid #000; padding:6px; vertical-align:middle; }
            .order-table th { background:#e0e0e0; text-align:center; }
            .text-center { text-align:center; }
            .text-right { text-align:right; }
            @media print {
              .d-print-none { display:none !important; }
              @page { size:A4; margin:0.5cm; }
            }
          </style>
        </head>
        <body>${printContent}</body>
        </html>
      `);

      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 500);
    },
  },

  mounted() {
    const orderId = this.$route.params.id;
    if (orderId) this.fetchBuyerOrder(orderId);
    else console.error("Order ID not found in route params");
  },
};
</script>

<style scoped>
  .order-paper { background:white; font-family:"Times New Roman", serif; min-height:297mm; }
  .meta-box { border:1px solid #000; padding:8px; font-size:13px; margin-bottom:16px; }
  .order-table { width:100%; border-collapse:collapse; font-size:13px; }
  .order-table th, .order-table td { border:1px solid #000; padding:6px; vertical-align:middle; }
  .order-table th { background:#e0e0e0; text-align:center; }
  .text-center { text-align:center; }
  .text-right { text-align:right; }
  .d-flex { display:flex; gap:10px; }
  .flex-1 { flex:1; }

  @media print {
    .d-print-none { display:none !important; }
    @page { size:A4; margin:0.5cm; }
  }
</style>
